import EF from 'ef/ef';
import utils from 'lib/utils';
import CourseSettingsService from 'services/course-settings-service';
import LocaleService from 'services/locale-service';
import VideoServiceBase from 'services/video-service-base';


/*
 * Handles both HLS streaming and a low resolution fallback
 *   If the HLS specific code gets too bad,
 *   consider splitting this apart into two HLS and fallback classes
*/
export default class VideoServiceNative extends VideoServiceBase {
  constructor(view) {
    super(view);
    view.model.property('height', height => this.video.height = height);
    view.model.property('width', width => this.video.width = width);

    Chaplin.mediator.subscribe('media:captions:change', this.toggleCaptions.bind(this));
  }

  canPlayHls() {
    const canPlay = this.video.canPlayType('application/vnd.apple.mpegURL');
    return canPlay === 'maybe' || canPlay === 'probably';
  }

  buildVideoPlayer(metadata, options) {
    const info = super.buildVideoPlayer(metadata, options);
    let source = document.createElement('source');
    let track = document.createElement('track');
    if(this.canPlayHls()) {
      source.src = `${info.mediaHost}/${info.transcode.OutputKeyPrefix}playlist.m3u8`;
      track.src = `${info.mediaHost}/${info.transcode.OutputKeyPrefix}captions-${i18next.language}.m3u8`;
    } else {
      const fallback = info.transcode.Outputs.find((transcode) => {
        return transcode.Key.startsWith('mp4');
      });
      if(!fallback) {
        return false;
      }
      source.type = 'video/mp4';
      source.src = `${info.mediaHost}/${info.transcode.OutputKeyPrefix}${fallback.Key}`;
      track.src = track.src = `${info.mediaHost}/${info.transcode.OutputKeyPrefix}captions-${i18next.language}.vtt`;
    }
    track.kind = 'subtitles';
    track.srclang = i18next.language;
    track.label = i18next.language;

    this.video.appendChild(source);
    this.video.appendChild(track);
    this.initializeMuteState();
    return true;
  }

  setupEvents(callbacks) {
    super.setupEvents();
    this.video.addEventListener('canplay', this.onReady.bind(this));
    this.video.addEventListener('play', callbacks.play);
    this.video.addEventListener('pause', callbacks.pause);
    this.video.addEventListener('progress', this.onTime.bind(this));
    this.video.addEventListener('ended', callbacks.end);
    this.video.addEventListener('seeking', this.onSeek.bind(this));
    this.destroyEvents = () => {
      this.video.removeEventListener('canplay', this.onReady);
      this.video.removeEventListener('play', callbacks.play);
      this.video.removeEventListener('pause', callbacks.pause);
      this.video.removeEventListener('progress', this.onTime);
      this.video.removeEventListener('ended', callbacks.end);
      this.video.removeEventListener('seeking', this.onSeek);
    };
  }

  toggleCaptions() {
    var tracks = Array.from(this.video.textTracks);
    if(this.canPlayHls()) {
      // Safari adds a null track at the 0th position, this will remove it and ignore it
      tracks.shift();
    }
    var index = tracks.findIndex(track => track.mode === 'showing');
    var length = tracks.length;

    if (index + 1 >= length) {
      if(this.canPlayHls()) {
        // Safari uses 'disabled' while Chrome and others use 'hidden'
        tracks.forEach(track => track.mode = 'disabled');
      } else  {
        tracks.forEach(track => track.mode = 'hidden');
      }
    } else {
      tracks[index + 1].mode = 'showing';
    }
  }

  onSeek() {
    if(!EF.options.local && this.progress) {
      if(this.video.currentTime > this.progress) {
        this.seek(this.currentTime);
      }
    }
  }

  onTime(e) {
    if(this.video.currentTime > this.progress) {
      this.progress = this.video.currentTime;
    }
    this.currentTime = this.video.currentTime;
  }

  play() {
    if (this.video) {
      this.video.play();
    }
  }

  pause() {
    if (this.video) {
      this.video.pause();
    }
  }

  mute() {
    if (this.video) {
      this.video.muted = true;
    }
  }

  unmute() {
    if (this.video) {
      this.video.muted = false;
    }
  }

  getState() {
    if (!this.video){
      return 'STOPPED';
    } else if (this.video.paused) {
      return 'PAUSED';
    } else {
      return 'PLAYING';
    }
  }

  getPosition() {
    return this.video ? this.video.currentTime() : 0;
  }

  getDuration() {
    return this.video ? this.video.duration : 0;
  }

  seek(position) {
    if (this.video) {
      this.video.currentTime = position;
    }
  }

  dispose() {
    if(this.destroyEvents) {
      this.destroyEvents();
    }
    super.dispose();
  }
}


